import React, { useState, useEffect } from 'react';
import {
  TextField,
  Divider,
  Alert,
  Autocomplete,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Rating,
} from '@mui/material';
import { CallMadeOutlined, ExpandMore, WarningAmber } from '@mui/icons-material';
import OrderFormLayout from '../../components/orders/OrderFormLayout';
import OutboundItemsTable, {
  OutboundOrderItem,
  AvailableItem,
} from '../../components/orders/OutboundItemsTable';
import { api } from '../../services/api';
import { Partner, OrderDirection } from '../../types/api';
import { useNotification } from '../../contexts/NotificationContext';
import { ErrorCodes } from '../../constants/errorCodes';

interface LimitExceededDetail {
  item_id: number;
  item_name: string;
  requested_quantity: number;
  monthly_limit: number;
  already_shipped: number;
  remaining: number;
}

interface UnfulfilledItem {
  item_id: number;
  item_name: string;
  category: string;
  unit: string;
  order_qty: number;
  shipped_qty: number;
  unfulfilled_qty: number;
  fulfill_quantity?: number;
}

interface UnfulfilledOrder {
  id: number;
  direction: string;
  partner_id: number;
  partner_name: string;
  status: string;
  created_at: string;
  items: UnfulfilledItem[];
}

const OutboundOrderCreate: React.FC = () => {
  const { alert } = useNotification();
  
  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState<Partner | null>(null);
  const [remark, setRemark] = useState('');
  const [selectedTransport, setSelectedTransport] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Customers state
  const [customers, setCustomers] = useState<Partner[]>([]);
  
  // Items state
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [orderItems, setOrderItems] = useState<OutboundOrderItem[]>([
    {
      item_id: 0,
      item_name: '',
      currentStock: null,
      orderQuantity: 0,
      pendingQuantity: 0,
      prevPendingQuantity: null,
      note: '',
      loadingStock: false,
    },
  ]);

  // Unfulfilled orders state
  const [unfulfilledOrders, setUnfulfilledOrders] = useState<UnfulfilledOrder[]>([]);
  const [loadingUnfulfilled, setLoadingUnfulfilled] = useState(false);

  // Fetch customers and items on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, itemsRes] = await Promise.all([
          api.get('/api/partners', { params: { type: 'CUSTOMER' } }),
          api.get('/api/items'),
        ]);
        setCustomers(customersRes.data.partners || []);
        setAvailableItems((itemsRes.data.items || []).map((item: any) => ({
          ...item,
          units_per_carton: item.metadata?.units_per_carton ?? 0,
        })));
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      }
    };
    fetchData();
  }, []);

  const handleFulfillQuantityChange = (orderId: number, itemId: number, value: string) => {
    // Allow only digits
    const onlyDigits = value.replace(/[^\d]/g, '');
    const numValue = onlyDigits === '' ? 0 : Number(onlyDigits);
    
    setUnfulfilledOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;
        return {
          ...order,
          items: order.items.map((item) => {
            if (item.item_id !== itemId) return item;
            // Ensure value doesn't exceed unfulfilled quantity
            const validValue = Math.min(Math.max(0, numValue), item.unfulfilled_qty);
            return { ...item, fulfill_quantity: validValue };
          }),
        };
      })
    );
  };

  const validateForm = (): boolean => {
    // Check if customer is selected
    if (!selectedCustomer) {
      setError('Please select a customer');
      return false;
    }

    // Check if at least one item with valid quantity (orderQuantity OR pendingQuantity)
    const selectedItems = orderItems.filter((item) => item.item_id > 0);
    
    if (selectedItems.length === 0) {
      setError('Please add at least one item');
      return false;
    }

    // Check if ALL selected items have either Order Qty or Pending Qty as non-zero
    const invalidItems = selectedItems.filter(
      (item) => item.orderQuantity === 0 && item.pendingQuantity === 0
    );
    
    if (invalidItems.length > 0) {
      setError('All items must have either Order Qty or Pending Qty as non-zero');
      return false;
    }

    // Check stock validation (only for items with orderQuantity)
    const invalidStockItems = orderItems.filter(
      (item) =>
        item.item_id > 0 &&
        item.orderQuantity > 0 &&
        item.currentStock !== null &&
        item.orderQuantity > item.currentStock
    );

    if (invalidStockItems.length > 0) {
      setError('Order quantity cannot exceed available stock');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    // Build shipment_for_previous_orders from unfulfilled orders
    const shipmentForPreviousOrders = unfulfilledOrders
      .map((order) => {
        const itemsWithQuantity = order.items
          .filter((item) => item.fulfill_quantity && item.fulfill_quantity > 0)
          .map((item) => ({
            item_id: item.item_id,
            quantity: item.fulfill_quantity,
          }));
        
        if (itemsWithQuantity.length === 0) return null;
        
        return {
          order_id: order.id,
          items: itemsWithQuantity,
        };
      })
      .filter((order): order is { order_id: number; items: { item_id: number; quantity: number | undefined }[] } => order !== null);

    const payload = {
      direction: OrderDirection.OUTWARD,
      partner_id: selectedCustomer?.id,
      metadata: {
        ...(remark && { note: remark }),
        ...(selectedTransport && { transport_provider: selectedTransport }),
      },
      items: orderItems
        .filter((item) => item.item_id > 0)
        .map((item) => ({
          item_id: item.item_id,
          order_qty: item.orderQuantity,
          pending_qty: item.pendingQuantity,
          note: item.note,
        })),
      ...(shipmentForPreviousOrders.length > 0 && { shipment_for_previous_orders: shipmentForPreviousOrders }),
    };

    try {
      const response = await api.post('/api/orders', payload);
      const orderId = response.data?.id || response.data?.order?.id;
      // Reset form
      setSelectedCustomer(null);
      setRemark('');
      setSelectedTransport('');
      setOrderItems([{
        item_id: 0,
        item_name: '',
        currentStock: null,
        orderQuantity: 0,
        pendingQuantity: 0,
        prevPendingQuantity: null,
        note: '',
        loadingStock: false,
      }]);
      setUnfulfilledOrders([]);
      // Show success dialog
      await alert({
        title: 'Order Created',
        message: `Order #${orderId || ''} created successfully!`,
        icon: 'success',
      });
    } catch (err: any) {
      const errorMessage = err.message|| 'Failed to create order';
      if (err.data?.error === ErrorCodes.LIMIT_EXCEEDED && Array.isArray(err.data?.data)) {
        const details: LimitExceededDetail[] = err.data.data;
        await alert({
          title: 'Limit Exceeded',
          message: errorMessage,
          icon: 'error',
          content: (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }} align="right">Ordered</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }} align="right">Remaining</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }} align="right">Monthly Limit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((d) => (
                    <TableRow key={d.item_id}>
                      <TableCell sx={{ py: 0.5 }}>{d.item_name}</TableCell>
                      <TableCell sx={{ py: 0.5, color: 'error.main', fontWeight: 600 }} align="right">{d.requested_quantity}</TableCell>
                      <TableCell sx={{ py: 0.5 }} align="right">{d.remaining}</TableCell>
                      <TableCell sx={{ py: 0.5 }} align="right">{d.monthly_limit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ),
        });
      } else {
        setError(errorMessage);
      }
      console.error('Error creating order:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrderFormLayout
      title="Create Outward Order"
      icon={<CallMadeOutlined sx={{ fontSize: 28 }} />}
      onSubmit={handleSubmit}
      submitLabel="Create Outward Order"
      loadingLabel="Creating..."
      loading={loading}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Customer Selection */}
      <Autocomplete
        fullWidth
        options={customers}
        getOptionLabel={(option) => option.name}
        value={selectedCustomer}
        onChange={(_, newValue) => {
          setSelectedCustomer(newValue);
          setSelectedTransport('');
        }}
        filterOptions={(options, { inputValue }) => {
          const filterValue = inputValue.toLowerCase();
          return options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Customer"
            placeholder="Type to search customer..."
            required
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {selectedCustomer && selectedCustomer.rating != null && selectedCustomer.rating > 0 && (
                    <Rating value={selectedCustomer.rating} size="small" readOnly sx={{ mr: 1 }} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
            </Box>
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Unfulfilled Orders Section */}
      {selectedCustomer && unfulfilledOrders.length > 0 && (
        <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              bgcolor: 'warning.50',
              borderRadius: 1,
              '&.Mui-expanded': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmber color="warning" />
              <Typography fontWeight={600}>
                Previous Unfulfilled Orders
              </Typography>
              <Chip
                label={`${unfulfilledOrders.length} order${unfulfilledOrders.length > 1 ? 's' : ''}`}
                size="small"
                color="warning"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {unfulfilledOrders.map((order) => (
              <Box key={order.id} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Order #{order.id}
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ borderTop: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Unfulfilled Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Fulfill Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item) => (
                        <TableRow key={item.item_id} hover>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.unfulfilled_qty}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="text"
                              inputMode="numeric"
                              size="small"
                              value={item.fulfill_quantity || ''}
                              onChange={(e) =>
                                handleFulfillQuantityChange(
                                  order.id,
                                  item.item_id,
                                  e.target.value
                                )
                              }
                              inputProps={{
                                min: 0,
                                max: item.unfulfilled_qty,
                                style: { textAlign: 'center', width: 80 },
                              }}
                              placeholder="0"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {loadingUnfulfilled && selectedCustomer && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Checking for unfulfilled orders...
        </Typography>
      )}

      {/* Items Table with Search, Stock & Quantities */}
      <OutboundItemsTable
        items={orderItems}
        availableItems={availableItems}
        onItemsChange={setOrderItems}
        partnerId={selectedCustomer?.id}
      />

      <Divider sx={{ my: 2 }} />

      {/* Transport Preference (Optional) */}
      {selectedCustomer && (selectedCustomer.metadata?.preferred_transports?.length ?? 0) > 0 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="transport-label">Transport Preference (Optional)</InputLabel>
          <Select
            labelId="transport-label"
            value={selectedTransport}
            label="Transport Preference (Optional)"
            onChange={(e) => setSelectedTransport(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {selectedCustomer.metadata?.preferred_transports?.map((transport: string) => (
              <MenuItem key={transport} value={transport}>
                {transport}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Remark */}
      <TextField
        fullWidth
        label="Order Notes"
        multiline
        rows={2}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        placeholder="Add any special instructions or notes"
      />
    </OrderFormLayout>
  );
};

export default OutboundOrderCreate;
